import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react'

import { useAbortController } from 'core/ui/hooks/useAbortController'

import { type SuggestedPreferredLane } from '../../domain/SuggestedPreferredLane'
import { useSuggestedPreferredLanes } from '../hooks/useSuggestedPreferredLanes'

type ContextType =
  | {
      preferredLaneSuggestions: SuggestedPreferredLane[]
      setPreferredLaneSuggestions: Dispatch<SetStateAction<SuggestedPreferredLane[]>>
    }
  | undefined

export const SuggestedPreferredLanesContext = createContext<ContextType>(undefined)
SuggestedPreferredLanesContext.displayName = 'SuggestedPreferredLanes'

export function useSuggestedPreferredLanesContext() {
  const context = useContext(SuggestedPreferredLanesContext)

  if (context === undefined) {
    throw new Error(
      `useSuggestedPreferredLaneContext must be used within a SuggestedPreferredLanesProvider`
    )
  }

  return context
}

export function SuggestedPreferredLanesProvider({ children }: { children?: React.ReactNode }) {
  const abortController = useAbortController()
  const { getSuggestedPreferredLanes } = useSuggestedPreferredLanes()

  const [preferredLaneSuggestions, setPreferredLaneSuggestions] = useState<
    SuggestedPreferredLane[]
  >([])

  useEffect(() => {
    async function onMount() {
      let suggestions: SuggestedPreferredLane[] = []

      try {
        suggestions = await getSuggestedPreferredLanes()
      } finally {
        if (!abortController.signal.aborted) {
          setPreferredLaneSuggestions(suggestions)
        }
      }
    }
    onMount()
  }, [abortController, getSuggestedPreferredLanes])

  const value = useMemo(
    () => ({ preferredLaneSuggestions, setPreferredLaneSuggestions }),
    [preferredLaneSuggestions, setPreferredLaneSuggestions]
  )

  return (
    <SuggestedPreferredLanesContext.Provider value={value}>
      {children}
    </SuggestedPreferredLanesContext.Provider>
  )
}
